<template>
    <Toast />
    <ConfirmDialog />
    <div class="grid">
        <div class="col-12">
            <div class="card">
                <div class="flex flex-column md:flex-row md:justify-content-between md:align-items-center mb-3">
                    <div>
                        <h5 class="m-0">Cierre de Caja</h5>
                        <small class="text-color-secondary">Compara los valores teóricos del sistema con lo que efectivamente se cuenta o se cobró.</small>
                    </div>
                    <Button v-if="isAdmin" label="Ver historial" icon="pi pi-list" class="p-button-text mt-2 md:mt-0" @click="$router.push('/dashboard/cierre-caja/historial')" />
                </div>

                <ProgressBar v-if="loading" mode="indeterminate" style="height: 4px" class="mb-3" />

                <Steps v-if="!loading && previewData" :model="stepsModel" :readonly="true" v-model:activeStep="currentStep" :data-active="currentStep" class="mb-4 closing-steps" />

                <!-- STEP 1: Resumen del periodo -->
                <div v-if="!loading && previewData && currentStep === 0">
                    <h6 class="mt-2">Resumen del período</h6>
                    <div class="grid">
                        <div class="col-12 md:col-6">
                            <div class="surface-100 border-round p-3">
                                <div class="text-color-secondary text-sm">Período</div>
                                <div class="font-medium mt-1">
                                    <span v-if="previewData.periodStart">{{ formatDate(previewData.periodStart) }}</span>
                                    <span v-else><i>Primer cierre</i></span>
                                    <i class="pi pi-arrow-right mx-2 text-xs"></i>
                                    <span>{{ formatDate(previewData.periodEnd) }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="col-12 md:col-6">
                            <div class="surface-100 border-round p-3">
                                <div class="text-color-secondary text-sm">Realizado por</div>
                                <div class="font-medium mt-1">{{ userLabel }} <Tag :value="previewData.creatorRol" severity="info" class="ml-2" /></div>
                            </div>
                        </div>
                    </div>

                    <!-- Ventas por método (H58): solo llega para el Administrador; el Operador
                         cuenta a ciegas, sin los montos esperados. -->
                    <div v-if="salesByMethod.length" class="grid mt-3">
                        <div class="col-12">
                            <div class="surface-100 border-round p-3">
                                <div class="text-color-secondary text-sm mb-2">Ventas por método</div>
                                <div v-for="row in salesByMethod" :key="row.code" class="flex justify-content-between text-sm py-1">
                                    <span>{{ paymentMethodLabel(row.code) }} <span class="text-color-secondary">({{ row.count }})</span></span>
                                    <span class="font-medium">$ {{ row.total }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="grid mt-3">
                        <div class="col-12">
                            <div class="surface-100 border-round p-3">
                                <div class="flex align-items-center mb-2">
                                    <i class="pi pi-inbox mr-2 text-orange-500"></i>
                                    <span class="text-color-secondary text-sm">Ventas abiertas en el período</span>
                                    <Tag :value="openSales.length" severity="warning" class="ml-2" />
                                </div>
                                <div v-if="openSales.length === 0" class="text-sm text-color-secondary py-2"><i>No hay ventas abiertas en este período</i></div>
                                <DataTable
                                    v-else
                                    :value="openSales"
                                    responsiveLayout="scroll"
                                    class="p-datatable-sm clickable-rows"
                                    :paginator="openSales.length > 10"
                                    :rows="10"
                                    @row-click="onSaleRowClick"
                                >
                                    <Column field="id" header="#" style="width: 5rem">
                                        <template #body="{ data }">
                                            #{{ data.id }} <i class="pi pi-external-link ml-1 text-xs text-color-secondary"></i>
                                        </template>
                                    </Column>
                                    <Column field="counterpartName" header="Cliente">
                                        <template #body="{ data }">
                                            {{ data.counterpartName || 'Consumidor final' }}
                                        </template>
                                    </Column>
                                    <Column field="paymentMethod" header="Método">
                                        <template #body="{ data }">
                                            {{ paymentMethodLabel(data.paymentMethod) }}
                                        </template>
                                    </Column>
                                    <Column field="total" header="Total" style="text-align: left" headerStyle="text-align: left">
                                        <template #body="{ data }">
                                            {{ formatMoney(data.total) }}
                                        </template>
                                    </Column>
                                    <Column field="createdAt" header="Fecha">
                                        <template #body="{ data }">
                                            {{ formatDate(data.createdAt) }}
                                        </template>
                                    </Column>
                                </DataTable>
                            </div>
                        </div>
                    </div>

                    <Message v-if="openSales.length > 0" severity="info" :closable="false" class="mt-3">
                        Si necesita gestionar alguna venta abierta antes del cierre, ábrala en una nueva pestaña, complete la operación y luego recargue esta vista.
                    </Message>

                    <div class="flex justify-content-end mt-4">
                        <Button label="Continuar" icon="pi pi-arrow-right" iconPos="right" :disabled="!previewData.lines || previewData.lines.length === 0" @click="goToStep(1)" />
                    </div>
                    <Message v-if="!previewData.lines || previewData.lines.length === 0" severity="warn" :closable="false" class="mt-3">
                        No hay cuentas financieras disponibles para cerrar.
                    </Message>
                </div>

                <!-- STEP 2: Declaración por cuenta -->
                <div v-if="!loading && previewData && currentStep === 1">
                    <h6 class="mt-2">Declaración por cuenta</h6>
                    <p class="text-color-secondary text-sm">Ingrese el valor real para cada cuenta de acuerdo a la pregunta indicada.</p>

                    <div v-for="line in lines" :key="line.accountId" class="card mb-3" style="box-shadow: none; border: 1px solid var(--surface-300);">
                        <div class="flex flex-column md:flex-row md:justify-content-between md:align-items-start gap-3">
                            <div class="flex-1">
                                <div class="font-bold text-lg">{{ line.accountName }}</div>
                                <div class="text-color-secondary text-sm mb-2">
                                    <Tag :value="line.moneyTypeLabel" severity="secondary" class="mr-2" />
                                    <span v-if="line.bank">{{ line.bank }}</span>
                                </div>
                                <div class="text-sm font-medium">{{ line.questionLabel }}</div>
                                <div class="text-color-secondary text-xs mt-1" v-if="line.evaluationMode === 'COLLECTED_INFLOWS'">
                                    Modo: cobros del período (entradas registradas)
                                </div>
                                <div class="text-color-secondary text-xs mt-1" v-else>
                                    Modo: saldo total registrado en sistema
                                </div>
                            </div>
                            <div class="md:w-15rem">
                                <label :for="'actual_' + line.accountId" class="block text-sm mb-1">Valor declarado</label>
                                <InputNumber
                                    :id="'actual_' + line.accountId"
                                    v-model="line.actualValue"
                                    mode="currency"
                                    currency="USD"
                                    locale="en-US"
                                    :min="0"
                                    :minFractionDigits="2"
                                    class="w-full"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="field mt-3">
                        <label for="notes" class="block">Notas (opcional)</label>
                        <Textarea id="notes" v-model="notes" rows="3" class="w-full" placeholder="Observaciones, justificaciones, novedades..." />
                    </div>

                    <Message severity="warn" :closable="false" class="mt-3">
                        <b>Aviso:</b> al cerrar la caja se generará un registro histórico inmutable y se enviará el reporte por correo a los destinatarios configurados. Revise los valores ingresados antes de continuar.
                    </Message>

                    <div class="flex justify-content-between mt-4">
                        <Button label="Atrás" icon="pi pi-arrow-left" class="p-button-text" @click="goToStep(0)" />
                        <Button label="Cerrar caja" icon="pi pi-check" class="p-button-success" :loading="saving" @click="confirmSave" />
                    </div>
                </div>

                <!-- STEP 3: Resultado (resumen + confirmación) -->
                <div v-if="!loading && previewData && currentStep === 2">
                    <div v-if="savedClosing" class="text-center my-4">
                        <i class="pi pi-check-circle text-green-500" style="font-size: 3.5rem"></i>
                        <h4 class="mt-3 mb-1">Cierre #{{ savedClosing.id }} guardado</h4>
                        <p class="text-color-secondary m-0">El reporte se está enviando automáticamente a los destinatarios configurados.</p>
                        <Tag :value="resultLabelByCode(savedClosing.result)" :severity="resultSeverityByCode(savedClosing.result)" class="mt-2" />
                    </div>

                    <div class="grid mt-3">
                        <div class="col-12 md:col-6">
                            <div class="surface-100 border-round p-3 h-full">
                                <div class="text-color-secondary text-sm">Período</div>
                                <div class="font-medium mt-1">
                                    <span v-if="periodStartLabel">{{ periodStartLabel }}</span>
                                    <span v-else><i>Primer cierre</i></span>
                                    <i class="pi pi-arrow-right mx-2 text-xs"></i>
                                    <span>{{ periodEndLabel }}</span>
                                </div>
                                <div v-if="savedClosing && savedClosing.createdAt" class="text-color-secondary text-xs mt-2">
                                    Registrado: {{ formatDate(savedClosing.createdAt) }}
                                </div>
                            </div>
                        </div>
                        <div class="col-12 md:col-6">
                            <div class="surface-100 border-round p-3 h-full">
                                <div class="text-color-secondary text-sm">Realizado por</div>
                                <div class="font-medium mt-1">
                                    {{ savedClosing && savedClosing.creator ? savedClosing.creator : userLabel }}
                                    <Tag :value="savedClosing && savedClosing.creatorRol ? savedClosing.creatorRol : previewData.creatorRol" severity="info" class="ml-2" />
                                </div>
                                <div v-if="notes" class="text-color-secondary text-xs mt-2">
                                    <b>Notas:</b> {{ notes }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <h6 class="mt-3">Detalle por cuenta</h6>
                    <DataTable :value="lines" responsiveLayout="scroll" class="p-datatable-sm">
                        <Column field="accountName" header="Cuenta" />
                        <Column field="moneyTypeLabel" header="Tipo" />
                        <Column header="Teórico">
                            <template #body="{ data }">{{ formatMoney(data.theoreticalValue) }}</template>
                        </Column>
                        <Column header="Práctico">
                            <template #body="{ data }">{{ formatMoney(data.actualValue) }}</template>
                        </Column>
                        <Column header="Diferencia">
                            <template #body="{ data }">
                                <span :class="resultClass(diffOf(data))">{{ formatMoney(diffOf(data)) }}</span>
                            </template>
                        </Column>
                        <Column header="Resultado">
                            <template #body="{ data }">
                                <Tag :value="resultLabel(diffOf(data))" :severity="resultSeverity(diffOf(data))" />
                            </template>
                        </Column>
                    </DataTable>

                    <div class="surface-100 border-round p-3 mt-3">
                        <div class="grid">
                            <div class="col-4 text-center">
                                <div class="text-color-secondary text-sm">Total teórico</div>
                                <div class="font-bold text-xl">{{ formatMoney(totals.theoretical) }}</div>
                            </div>
                            <div class="col-4 text-center">
                                <div class="text-color-secondary text-sm">Total práctico</div>
                                <div class="font-bold text-xl">{{ formatMoney(totals.actual) }}</div>
                            </div>
                            <div class="col-4 text-center">
                                <div class="text-color-secondary text-sm">Diferencia global</div>
                                <div class="font-bold text-xl" :class="resultClass(totals.diff)">{{ formatMoney(totals.diff) }}</div>
                                <Tag :value="resultLabel(totals.diff)" :severity="resultSeverity(totals.diff)" class="mt-1" />
                            </div>
                        </div>
                    </div>

                    <div class="flex justify-content-center gap-2 mt-4">
                        <Button v-if="savedClosing" label="Realizar otro cierre" icon="pi pi-refresh" class="p-button-outlined" @click="restart" />
                        <Button v-if="savedClosing && isAdmin" label="Ver historial" icon="pi pi-list" @click="$router.push('/dashboard/cierre-caja/historial')" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import httpRequest from '../../../service/httpRequest';
import { salesByMethodRows } from '../../../service/cashClosingSummary';

export default {
    name: 'CashClosingWizard',
    data() {
        return {
            loading: false,
            saving: false,
            currentStep: 0,
            previewData: null,
            lines: [],
            notes: '',
            savedClosing: null,
            stepsModel: [
                { label: 'Resumen' },
                { label: 'Declaración' },
                { label: 'Resultado' }
            ]
        };
    },
    created() {
        this.loadPreview();
    },
    computed: {
        isAdmin() {
            // Espejo del decorador backend: ver historial es admin-only (el Operador solo crea).
            return this.$can('cashClosingManage');
        },
        userLabel() {
            const user = this.$store.getters.getUser;
            if (!user) return '-';
            return user.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : user.username;
        },
        totals() {
            let theoretical = 0, actual = 0;
            for (const l of this.lines) {
                theoretical += Number(l.theoreticalValue || 0);
                actual += Number(l.actualValue || 0);
            }
            return {
                theoretical: theoretical,
                actual: actual,
                diff: actual - theoretical
            };
        },
        openSales() {
            return (this.previewData && this.previewData.summary && this.previewData.summary.openSales) || [];
        },
        salesByMethod() {
            const byMethod = this.previewData && this.previewData.summary && this.previewData.summary.salesByPaymentMethod;
            return salesByMethodRows(byMethod);
        },
        periodStartLabel() {
            const s = (this.savedClosing && this.savedClosing.periodStart) || (this.previewData && this.previewData.periodStart);
            return s ? this.formatDate(s) : '';
        },
        periodEndLabel() {
            const s = (this.savedClosing && this.savedClosing.periodEnd) || (this.previewData && this.previewData.periodEnd);
            return s ? this.formatDate(s) : '';
        }
    },
    methods: {
        async loadPreview() {
            this.loading = true;
            try {
                const r = await httpRequest.getCashClosingPreview();
                if (r.status === 200 && r.data) {
                    this.previewData = r.data;
                    const moneyTypeOrder = { CASH: 0, TRANSFER: 1, CARD: 2 };
                    this.lines = (r.data.lines || [])
                        .map(l => ({
                            ...l,
                            theoreticalValue: Number(l.theoreticalValue || 0),
                            actualValue: 0
                        }))
                        .sort((a, b) => {
                            const oa = moneyTypeOrder[a.moneyType] !== undefined ? moneyTypeOrder[a.moneyType] : 99;
                            const ob = moneyTypeOrder[b.moneyType] !== undefined ? moneyTypeOrder[b.moneyType] : 99;
                            if (oa !== ob) return oa - ob;
                            return (a.accountName || '').localeCompare(b.accountName || '');
                        });
                } else if (r.status === 502) {
                    this.$toast.add({ severity: 'error', summary: 'No autorizado', detail: 'Inicie sesión nuevamente', life: 4000 });
                } else {
                    this.$toast.add({ severity: 'error', summary: 'Error', detail: r.error || 'No se pudo cargar la vista previa', life: 4000 });
                }
            } catch (e) {
                console.error(e);
                this.$toast.add({ severity: 'error', summary: 'Error', detail: 'Fallo de red', life: 4000 });
            } finally {
                this.loading = false;
            }
        },
        goToStep(step) {
            this.currentStep = step;
        },
        diffOf(line) {
            return Number(line.actualValue || 0) - Number(line.theoreticalValue || 0);
        },
        resultLabel(diff) {
            if (Number(diff) === 0) return 'Cuadrado';
            return diff > 0 ? 'Sobrante' : 'Faltante';
        },
        resultLabelByCode(code) {
            return { BALANCED: 'Cuadrado', OVERAGE: 'Sobrante', SHORTAGE: 'Faltante' }[code] || code;
        },
        resultSeverity(diff) {
            if (Number(diff) === 0) return 'success';
            return diff > 0 ? 'info' : 'danger';
        },
        resultSeverityByCode(code) {
            return { BALANCED: 'success', OVERAGE: 'info', SHORTAGE: 'danger' }[code] || 'info';
        },
        resultClass(diff) {
            if (Number(diff) === 0) return 'text-green-500 font-medium';
            return diff > 0 ? 'text-blue-500 font-medium' : 'text-red-500 font-medium';
        },
        formatMoney(v) {
            const n = Number(v || 0);
            return '$ ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        formatDate(s) {
            if (!s) return '-';
            try {
                const d = new Date(s);
                return d.toLocaleString('es-EC', { dateStyle: 'short', timeStyle: 'short' });
            } catch { return s; }
        },
        openSaleInNewTab(sale) {
            const route = this.$router.resolve({ name: 'saleForm', query: { id: sale.id } });
            window.open(route.href, '_blank');
        },
        onSaleRowClick(event) {
            if (event && event.data) this.openSaleInNewTab(event.data);
        },
        paymentMethodLabel(code) {
            return {
                CASH: 'Efectivo',
                TRANSFER: 'Transferencia',
                DEBIT_CARD: 'Tarjeta Débito',
                CREDIT_CARD: 'Tarjeta Crédito',
                CHECK: 'Cheque',
                MIXED: 'Mixto',
                CREDIT_NOTE: 'Nota de crédito',
                '': '—'
            }[code] || code;
        },
        confirmSave() {
            this.$confirm.require({
                message: '¿Confirmar el cierre de caja con estos valores? Esta acción quedará registrada y se enviará el reporte por correo.',
                header: 'Confirmar cierre',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'Confirmar',
                rejectLabel: 'Cancelar',
                accept: () => this.save()
            });
        },
        async save() {
            this.saving = true;
            try {
                const payloadLines = this.lines.map(l => ({
                    accountId: l.accountId,
                    actualValue: Number(l.actualValue || 0).toFixed(2)
                }));
                const r = await httpRequest.saveCashClosing(payloadLines, this.notes);
                if (r.status === 200 && r.data) {
                    this.savedClosing = r.data;
                    this.currentStep = 2;
                    this.$toast.add({ severity: 'success', summary: 'Cierre guardado', detail: `Cierre #${r.data.id} registrado`, life: 4000 });
                } else {
                    this.$toast.add({ severity: 'error', summary: 'Error', detail: r.error || 'No se pudo guardar el cierre', life: 5000 });
                }
            } catch (e) {
                console.error(e);
                this.$toast.add({ severity: 'error', summary: 'Error', detail: 'Fallo de red', life: 4000 });
            } finally {
                this.saving = false;
            }
        },
        restart() {
            this.currentStep = 0;
            this.previewData = null;
            this.lines = [];
            this.notes = '';
            this.savedClosing = null;
            this.loadPreview();
        }
    }
};
</script>

<style scoped>
.card {
    background: var(--surface-card);
}
.clickable-rows :deep(tbody tr) {
    cursor: pointer;
}
.clickable-rows :deep(tbody tr:hover) {
    background-color: var(--surface-100) !important;
}

/* Step indicator: highlight current and completed steps */
.closing-steps :deep(.p-steps-item .p-steps-number) {
    background: var(--surface-200);
    color: var(--text-color-secondary);
    border: 2px solid var(--surface-300);
}
.closing-steps :deep(.p-steps-item .p-steps-title) {
    color: var(--text-color-secondary);
}
.closing-steps[data-active="0"] :deep(.p-steps-item:nth-child(1) .p-steps-number),
.closing-steps[data-active="1"] :deep(.p-steps-item:nth-child(-n+2) .p-steps-number),
.closing-steps[data-active="2"] :deep(.p-steps-item:nth-child(-n+3) .p-steps-number) {
    background: var(--primary-color);
    color: var(--primary-color-text);
    border-color: var(--primary-color);
}
.closing-steps[data-active="0"] :deep(.p-steps-item:nth-child(1) .p-steps-title),
.closing-steps[data-active="1"] :deep(.p-steps-item:nth-child(-n+2) .p-steps-title),
.closing-steps[data-active="2"] :deep(.p-steps-item:nth-child(-n+3) .p-steps-title) {
    color: var(--text-color);
    font-weight: 600;
}
</style>
